import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Hampshire\'s GREEN condition scorecard mean I cannot claim?',
    answer: 'No. GREEN means Hampshire performs above the DfT threshold on network-level condition data — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The council\'s own report admits the overall condition of the carriageway network and the rate of deterioration is not fully reflected in the condition figures reported to the Department for Transport, and it recorded 91,661 highway defect repairs in 2022/23 alone.',
  },
  {
    question: 'Why is the overall rating AMBER when condition is GREEN?',
    answer: 'The DfT assigns separate scorecards. Hampshire is GREEN on condition but AMBER on spend and best practice, producing an AMBER overall rating. That pattern means published network condition looks comparatively strong while the Department for Transport still flags concerns about investment levels and documented maintenance practice. Your claim still turns on the specific defect, photos, prior reports and inspection intervals — not headline colours alone.',
  },
  {
    question: 'What does the 91,661 defect spike in 2022/23 mean for my claim?',
    answer: 'It is context, not a guarantee. In 2020/21 Hampshire repaired 25,701 highway defects; in 2022/23 that rose to 91,661 — a 256.6% increase on the council\'s own figures. Even after falling to 32,419 in 2024/25, repair volumes remain 26.1% above the 2020/21 baseline. That volatility shows reactive workload surging across England\'s fifth-largest network, which can support an argument that inspection and prevention were overwhelmed — but you still need evidence tying the pattern to your specific road and defect.',
  },
  {
    question: 'What if my pothole was on an unclassified or residential road?',
    answer: 'Unclassified roads make up 5,426km — 62% of Hampshire\'s 8,698km carriageway network. At the last published survey, 7.7% of U-roads were in RED condition, up from 6.8% in 2020/21. The council inspects U-roads annually and B/C roads every three to six months, but a defect can still form between visits — especially where the council acknowledges a significant maintenance backlog and an estimated £500 million highways backlog across the county.',
  },
  {
    question: 'Hampshire says the pothole was not reported before I hit it — does that defeat my claim?',
    answer: 'Not automatically. Hampshire has a proactive duty to inspect highways. If a defect existed between scheduled inspections, that may indicate the inspection frequency was inadequate for the road\'s condition, or that deterioration developed faster than the regime assumed. Prior public reports (FixMyStreet, the council portal) are still the strongest proof of actual notice — but the 91,661 defect-repair spike and council admissions about backlog and reactive workload can contextualise whether a "not reported" defence was reasonable on your road class.',
  },
  {
    question: 'Is Hampshire County Council the same authority as Portsmouth or Southampton?',
    answer: 'No. Hampshire County Council maintains county roads across Hampshire excluding the unitary cities of Portsmouth and Southampton, which have their own highway authorities. Motorways and some trunk A-roads are maintained by National Highways. If your pothole was in Winchester, Basingstoke, the New Forest or another Hampshire town, your claim is against the county council — not Portsmouth City Council or Southampton City Council.',
  },
  {
    question: 'How do I report a pothole to Hampshire County Council?',
    answer: 'Report potholes via the council\'s online form at hants.gov.uk/transport/highways/report-a-problem/potholes. For emergencies posing an immediate risk, call 0300 555 1388 (Mon–Fri 8:30am–5pm) or 101 out of hours. The council states a greater proportion of reported potholes are currently treated with temporary repairs before permanent work. Prior reports of the same defect strengthen a claim by demonstrating notice. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function HampshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hampshire Pothole Claims | 91,661 Defect Repairs in 2022/23 | Fixtyer"
        description="Hampshire County Council earns GREEN DfT condition scorecards on an 8,698km network — yet repaired 91,661 highway defects in 2022/23 and admits a significant maintenance backlog. See what the AMBER overall rating means for your claim."
        canonical="/councils/hampshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hampshire', url: '/councils/hampshire' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Hampshire County Council"
        councilSlug="hampshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/hampshire"
        headline="Hampshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hampshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            91,661 Highway Defect Repairs in 2022/23 on England&apos;s Fifth-Largest Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hampshire County Council — distinct from Portsmouth and Southampton city councils — manages{' '}
            <strong className="text-rose-400">8,698km of roads</strong>, England&apos;s fifth-largest highway network.
            The DfT rates condition <strong className="text-emerald-400">GREEN</strong> but spend and best practice{' '}
            <strong className="text-amber-400">AMBER</strong>, for an <strong className="text-amber-400">AMBER</strong>{' '}
            overall scorecard. Its own transparency report records a{' '}
            <strong className="text-rose-400">91,661 defect-repair spike in 2022/23</strong>, a{' '}
            <strong className="text-amber-400">significant maintenance backlog</strong>, and an admission that published
            condition figures may understate real deterioration. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">32,419</div>
            <div className="text-white font-medium mt-2">Highway defects repaired in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Down 59% from 79,573 in 2023/24 — yet still 26.1% above the 2020/21 baseline of 25,701, on a network
              where 5,426km of unclassified roads carry most residential claims.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            8,698km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Hampshire&apos;s June 2025 transparency report — excluding Portsmouth and Southampton
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">8,698km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">England&apos;s 5th largest network</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">5,426km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">62% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">2,600km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">672km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Some trunk routes are National Highways</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Scope caveat:</strong> Hampshire County Council maintains county roads across Hampshire but not
              roads inside Portsmouth or Southampton city boundaries, where unitary councils are the highway
              authorities. Motorways and some major A-roads (including parts of the M3, M27, A34 and A303) are
              maintained by National Highways.
            </p>
          </div>

          <QuoteCard
            quote="It is recognised nationally that the condition of local authority highway networks is deteriorating and that they are increasingly less resilient to the demands placed upon them. However, the overall condition of the carriageway network and the rate of deterioration is not reflected in the condition figures that are reported by the Department for Transport."
            source="Hampshire County Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What GREEN Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Published red/amber/green breakdowns from Hampshire&apos;s transparency report — with the council&apos;s own caveat
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-900 mb-2">A roads (672km)</h3>
              <div className="text-3xl font-bold text-amber-600">26%</div>
              <div className="text-sm text-surface-600 mt-1">Red or amber condition</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-900 mb-2">B and C roads (2,600km)</h3>
              <div className="text-3xl font-bold text-amber-600">29%</div>
              <div className="text-sm text-surface-600 mt-1">Red or amber condition</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-2">U roads (5,426km)</h3>
              <div className="text-3xl font-bold text-rose-600">7.7%</div>
              <div className="text-sm text-amber-800 mt-1">Red condition — up from 6.8% in 2020/21</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">GREEN scorecard, documented deterioration</h3>
            <p className="text-brand-200 mb-4">
              The DfT&apos;s GREEN condition rating reflects Hampshire&apos;s published network survey data meeting the
              national threshold. But the council itself states those reported figures do not fully reflect the rate
              of deterioration across the network — and U-road RED condition has risen while comprising 62% of
              carriageway length.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Roughly 418km of U-roads in RED condition at the last survey (7.7% of 5,426km)</li>
              <li>• A and B/C roads: over a quarter in red or amber condition on published data</li>
              <li>• Council estimates a £500 million highway maintenance backlog across the county</li>
            </ul>
          </div>

          <QuoteCard
            quote="To try and slow down the rate of deterioration, the County Council has invested significant levels of locally resourced funding over the last 15 years to supplement the insufficient funding provided by Government. However, even with this additional funding a significant backlog of maintenance work has built up across all major assets over the last few decades."
            source="Hampshire County Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Defect repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Defect-Repair Volatility
          </h2>
          <p className="text-surface-600 mb-8">
            Highway defects repaired — a broader category than pothole fills alone, from the council&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Hampshire highway defect repairs 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Defects repaired</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">25,701</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">25,069</td>
                  <td className="px-6 py-4 text-center text-surface-500">-2.5%</td>
                </tr>
                <tr className="bg-rose-50 border-b border-rose-200">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">91,661</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+256.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">79,573</td>
                  <td className="px-6 py-4 text-center text-amber-600">+209.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">32,419</td>
                  <td className="px-6 py-4 text-center text-amber-600">+26.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (to date)</td>
                  <td className="px-6 py-4 text-center font-bold">22,172</td>
                  <td className="px-6 py-4 text-center text-surface-500">Reactive and emergency repairs reported by council</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Reactive workload share rising</h3>
              <p className="text-surface-600 text-sm">
                Defect repairs classified as reactive work rose from <strong>12% of reactive budget in 2020/21</strong> to{' '}
                <strong>30% in 2023/24</strong> — a 150% increase in the share of reactive spending consumed by emergency
                defect response, on the council&apos;s own figures.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Council explanation for the 2024/25 drop</h3>
              <p className="text-amber-800 text-sm">
                Hampshire says it is taking a more strategic maintenance approach, planning to invest{' '}
                <strong>£55.9 million in highways maintenance in 2025/26</strong> and targeting planned work to reduce
                long-term reactive costs. Lower repair counts do not prove your specific road was maintained to a
                reasonable standard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending and resurfacing */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN condition but AMBER spend — projected resurfacing down 53.4% from its 2020/21 peak
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-3xl font-bold text-emerald-400">£52.9m</div>
              <div className="text-brand-300 text-sm">DfT highways allocation 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">£55.9m</div>
              <div className="text-brand-300 text-sm">Planned highways maintenance spend 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-amber-400">£10m/yr</div>
              <div className="text-brand-300 text-sm">Extra local planned maintenance since 2010</div>
            </div>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Hampshire planned carriageway resurfacing 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">km resurfaced</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">% of 8,698km network</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">313.2</td>
                  <td className="px-4 py-3 text-center">3.6%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">276.8</td>
                  <td className="px-4 py-3 text-center">3.2%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">153.2</td>
                  <td className="px-4 py-3 text-center">1.76%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">191.8</td>
                  <td className="px-4 py-3 text-center">2.2%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">236.4</td>
                  <td className="px-4 py-3 text-center">2.7%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">146.1</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">1.68%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why condition is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Hampshire&apos;s published survey data meets the DfT condition threshold nationally. The council also
                points to £132 million of long-term government resurfacing funding announced in 2024 and an extra{' '}
                £14.1 million in DfT allocation for 2025/26 — plus £10 million of locally resourced planned maintenance
                added annually since 2010.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is AMBER</h3>
              <p className="text-brand-200 text-sm">
                The DfT spend scorecard is AMBER despite rising government allocations. Planned resurfacing is projected
                at 146.1km in 2025/26 — down 53.4% from the 313.2km delivered in 2020/21. At that rate it would take
                roughly 59.5 years to resurface the full 8,698km network once, versus a 20-year industry benchmark.
                The council also cut planned maintenance by £7.5 million from April 2025 amid wider budget pressures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Hampshire says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> monthly safety inspections</li>
                <li>• <strong>B and C roads:</strong> every three to six months depending on hierarchy</li>
                <li>• <strong>U roads:</strong> annual inspections — 62% of the network</li>
                <li>• <strong>Reactive repairs:</strong> council states more potholes are currently receiving temporary repairs first</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Prevention vs reaction</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hampshire&apos;s transparency report states that if a road does not receive planned resurfacing capital
                investment, it is more likely that reactive work will be required to fix highway defects such as
                potholes — and that prevention is cheaper in the long term.
              </p>
              <p className="text-surface-600 text-sm">
                Yet reactive defect work&apos;s share of reactive budgets rose from 12% to 30% between 2020/21 and
                2023/24 while resurfacing delivery fell sharply from its 2020/21 peak.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Hampshire must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it
              must show a reasonable system for inspecting and repairing the specific defect — not just publish a GREEN
              condition scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the annual U-road cycle or a three-to-six-month B/C inspection schedule?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Providing sufficient capital investment aims to reduce more expensive reactive works in the long-term. For example, if a road does not receive the required capital investment for planned resurfacing work, it is more likely that an increase in reactive work will be required to fix highway defects such as potholes."
            source="Hampshire County Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Hampshire Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report and related council documents
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On funding inadequacy</h3>
              <QuoteCard
                quote="It is essential that Government funding for local highway authorities is increased, and longer-term funding settlements provided. This will enable longer-term and more effective maintenance strategies and programmes that will assist in addressing the increasing backlog of maintenance required on the network."
                source="Hampshire County Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On temporary repairs and high demand</h3>
              <p className="text-surface-600 text-sm bg-amber-50 border border-amber-200 rounded-xl p-6">
                Hampshire&apos;s pothole reporting page states the authority is facing high demand, that a greater
                proportion of reported potholes are currently treated with temporary repairs, and that between four and
                seven temporary repairs can be completed in the time taken for one permanent repair — enabling faster
                response to immediate safety risks while permanent work is deferred.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Condition, AMBER-Overall County
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hampshire is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN DfT condition scorecard on published survey data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £52.9m DfT allocation and £55.9m planned maintenance budget for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £132m long-term government resurfacing programme announced 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £10m annual local top-up for planned maintenance since 2010
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented inspection hierarchy on A, B/C and U roads
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence citing GREEN condition data and large-network scale.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER spend and best-practice scorecards from the DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits published condition figures understate deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 91,661 defect repairs in 2022/23 — 256.6% above 2020/21 baseline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 5,426km of U-roads (62%) with RED condition rising to 7.7%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing projected down 53.4% from 2020/21 peak; £500m backlog estimated
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> More temporary pothole repairs while permanent work is deferred
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a county with GREEN condition scorecards and £55.9m maintenance budgets, your claim lives or
              dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the annual inspection interval is your strongest structural argument</li>
              <li>• Council&apos;s own admission that DfT condition figures may not reflect real deterioration on your route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Hampshire&apos;s
              own transparency data where it helps you — without pretending the council is failing on every scorecard.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Hampshire County Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack. The council may treat more reports with temporary repairs
            while permanent work is scheduled.
          </p>
          <a
            href="https://www.hants.gov.uk/transport/highways/report-a-problem/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — hants.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Hampshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN condition scorecard demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road inspection-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No council condition-caveat citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 91,661 defect-repair spike documented</li>
                <li>✅ Council admission on DfT condition gap cited</li>
                <li>✅ 5,426km U-road network context included</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hampshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hampshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Hampshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://documents.hants.gov.uk/transport/DfT-HampshireCountyCouncil-TransparencyReport-2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hampshire County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
